import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styled from "@emotion/styled";
import { FC } from "react";

import { Delete } from "@livechat/design-system-icons";
import { Button, Icon, SpacingToken } from "@livechat/design-system-react-components";

import { EventPlace, trackEngagement } from "helpers/analytics";
import { FormBlockType } from "store/entities/contact-forms/interfaces";
import { withVar } from "themes/helpers";

import { FormBlock } from "./FormBlock";
import { BLOCK_PROPERTIES, LABEL_MAP } from "./constants";

type Props = {
  index: number;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  fieldLabel: string;
  fieldName: string;
  onRemove: (index: number) => void;
  fieldType: FormBlockType;
};

export const SortableField: FC<Props> = ({
  index,
  isOpen,
  onOpen,
  onClose,
  fieldLabel,
  fieldName,
  onRemove,
  fieldType,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: `item-${index}`,
  });
  if (transform) {
    transform.scaleY = 1;
  }
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: "translate3d(100px, 100px, 0)",
    zIndex: isDragging ? 999 : undefined,
  };
  return (
    <SortableElement key={`item-${index}`} ref={setNodeRef} style={style}>
      <FieldWrapper key={`item-${index}`}>
        <FormBlock
          attributes={attributes}
          listeners={listeners as SyntheticListenerMap}
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          fieldLabel={fieldLabel}
          fieldName={fieldName}
          fieldType={fieldType}
          isDragging={isDragging}
        />
      </FieldWrapper>
      {!BLOCK_PROPERTIES[fieldType]?.required && (
        <Button
          onClick={() => {
            trackEngagement("Settings: Contact form field removed", EventPlace.Settings, {
              fieldType: LABEL_MAP[fieldType],
            });
            onRemove(index);
          }}
          size="xcompact"
          kind="plain"
          className={`delete-button ${isDragging ? "dragging" : ""}`}
          data-testid="delete-button"
          icon={<Icon source={Delete} />}
        />
      )}
    </SortableElement>
  );
};

const SortableElement = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  width: 520px;

  .delete-button {
    right: 0px;
    top: 21px;
    position: absolute;
    visibility: hidden;
  }

  &:hover {
    .delete-button {
      visibility: visible;
    }
  }

  .delete-button.dragging {
    visibility: hidden;
  }
`;

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing4)};
  width: 482px;
  margin-bottom: ${withVar(SpacingToken.Spacing2)};
`;
